import {
  Button,
  ChatIcon,
  CheckIcon,
  Checkbox,
  Flex,
  Input,
  NotesIcon,
  Text,
} from "@minervaproject/fds";
import cuid from "cuid";
import { format } from "date-fns";
import { ChangeEvent, useContext, useState } from "react";
import { useDrag } from "react-dnd";
import TextareaAutosize from "react-textarea-autosize";

import { createPollHelper, createPollSessionHelper } from "api/api_helpers";
import { ForumAPIContext } from "contexts/forum_api_context";
import { useAppDispatch, useAppSelector, useArraySelector } from "hooks/redux_hooks";
import { collect, useDragHandler } from "hooks/use_drag_handler";
import { featureContentInOneUp } from "shared/reducers/classroom";
import {
  PaneContentType,
  PollChoiceType,
  PollSessionStatusType,
  PollSessionType,
  PollType,
  PollTypeType,
} from "shared/types";
import { classroomSelectors } from "store";

const FRIENDLY_POLL_TYPES = {
  [PollTypeType.LongForm]: "Long Form",
  [PollTypeType.MultipleChoice]: "Multiple Choice",
  [PollTypeType.YesNo]: "Yes/No",
};

function NewPollForm({
  pollType,
  hideCallback,
}: {
  pollType: PollTypeType;
  hideCallback: () => void;
}) {
  const dispatch = useAppDispatch();
  const forumAPI = useContext(ForumAPIContext);
  const classId = useAppSelector(classroomSelectors.selectClassId());
  const [pollForm, setPollForm] = useState({
    question: "",
    studentVisible: true,
    multipleResponse: false,
    choices: [""],
  });
  const [numChoices, setNumChoices] = useState(1);
  const [focusChoices, setFocusChoices] = useState(false);
  const questionLabel = `${FRIENDLY_POLL_TYPES[pollType]} Question`;

  const handleChangeQuestion = (e: ChangeEvent) => {
    setPollForm({ ...pollForm, question: (e.target as HTMLInputElement).value });
  };

  const handleChangeChoice = (index: number) => (choiceText: string) => {
    pollForm.choices[index] = choiceText;
    setPollForm({ ...pollForm, choices: pollForm.choices });
    if (index === numChoices - 1 && choiceText !== "") {
      setPollForm({ ...pollForm, choices: pollForm.choices.concat("") });
      setNumChoices(numChoices + 1);
    }
    if (index === numChoices - 2 && choiceText === "") {
      setPollForm({ ...pollForm, choices: pollForm.choices.slice(0, numChoices - 1) });
      setNumChoices(numChoices - 1);
    }
  };

  const handleBlurChoice = (index: number) => () => {
    if (pollForm.choices[index] === "" && index !== numChoices - 1) {
      setPollForm({
        ...pollForm,
        choices: pollForm.choices.slice(0, index).concat(pollForm.choices.slice(index + 1)),
      });
      setNumChoices(numChoices - 1);
    }
  };

  const handleBegin = async () => {
    let pollChoices: PollChoiceType[] = [];
    if (pollType === PollTypeType.MultipleChoice) {
      pollChoices = pollForm.choices
        .filter((choice) => choice !== "")
        .map((choice, index) => ({
          id: cuid(),
          choice,
          index,
        }));
    } else if (pollType === PollTypeType.YesNo) {
      pollChoices = ["Yes", "No"].map((choice, index) => ({
        id: cuid(),
        choice,
        index,
      }));
    }
    // TODO: It could be worth making a single API call to create Poll and PollSession
    // if this turns out to be slow in production.
    const newPoll = await createPollHelper(
      classId,
      pollForm.question,
      pollType,
      pollForm.studentVisible,
      pollForm.multipleResponse,
      pollChoices,
      dispatch,
      forumAPI,
    );
    const newPollSession = await createPollSessionHelper(newPoll.id, dispatch, forumAPI);
    dispatch(
      featureContentInOneUp({
        contentId: newPollSession.id,
        contentType: PaneContentType.PollSession,
      }),
    );
    hideCallback();
  };

  const formIsValid = () => {
    if (pollType === PollTypeType.MultipleChoice) {
      return (
        pollForm.question.length > 0 &&
        pollForm.choices.filter((choice) => choice !== "").length > 0
      );
    }
    return pollForm.question.length > 0;
  };

  return (
    <form
      className="flex flex-column"
      onSubmit={(e) => {
        e.preventDefault();
      }}
    >
      <label className="body-l text-white pb2" htmlFor="question">
        {questionLabel}
      </label>
      <TextareaAutosize
        autoFocus
        className="body-s p2"
        id="question"
        minRows={3}
        onChange={handleChangeQuestion}
        onFocus={() => {
          setFocusChoices(false);
        }}
        placeholder="Type your question here"
        value={pollForm.question}
      />
      {pollType === PollTypeType.MultipleChoice && (
        <>
          <h2 className="body-l text-white pb2 pt5">Choices</h2>
          {[...Array(numChoices)].map((_, index) => (
            <Input
              // eslint-disable-next-line react/no-array-index-key
              key={index}
              autoFocus={focusChoices && index === numChoices - 1}
              fullWidth
              mb={2}
              aria-label={`choice-${index + 1}`}
              onChange={handleChangeChoice(index)}
              onBlur={handleBlurChoice(index)}
              placeholder="Add a choice..."
              value={pollForm.choices[index]}
            />
          ))}
          <Checkbox
            mt={3}
            onChange={() => {
              setPollForm({ ...pollForm, multipleResponse: !pollForm.multipleResponse });
            }}
            value={pollForm.multipleResponse}
          >
            <Text color="white" kind="body-s">
              Allow multiple responses
            </Text>
          </Checkbox>
        </>
      )}
      <Checkbox
        mt={pollType === PollTypeType.MultipleChoice ? 2 : 5}
        onChange={() => {
          setPollForm({ ...pollForm, studentVisible: !pollForm.studentVisible });
        }}
        value={pollForm.studentVisible}
      >
        <Text color="white" kind="body-s" data-testid="poll-visibility-label">
          Students can see responses
        </Text>
      </Checkbox>
      <Button
        data-testid="begin-poll-button"
        kind="classroom-primary"
        onClick={handleBegin}
        disabled={!formIsValid()}
        mt={5}
      >
        Begin
      </Button>
      <Button
        kind="text-classroom-secondary"
        className="justify-center"
        onClick={hideCallback}
        mt={2}
      >
        Back
      </Button>
    </form>
  );
}

function PollButton({ poll }: { poll: PollType }) {
  const dispatch = useAppDispatch();
  const forumAPI = useContext(ForumAPIContext);
  const pollSessions = useArraySelector(classroomSelectors.selectPollSessionsByPollId(poll.id), [
    "status",
  ]);
  const inProgressPollSession = pollSessions.find(
    (pollSession) => pollSession.status === PollSessionStatusType.InProgress,
  );
  const title = poll.title || poll.question;

  const [{ isDragging }, drag] = useDrag({
    type: PaneContentType.PollSession,
    item: {
      callback: async () => {
        if (inProgressPollSession) {
          return inProgressPollSession.id;
        }
        const newPollSession = await createPollSessionHelper(poll.id, dispatch, forumAPI);
        return newPollSession.id;
      },
    },
    collect,
  });
  useDragHandler(isDragging);

  const startPollSession = async () => {
    if (inProgressPollSession) {
      dispatch(
        featureContentInOneUp({
          contentId: inProgressPollSession.id,
          contentType: PaneContentType.PollSession,
        }),
      );
    } else {
      const newPollSession = await createPollSessionHelper(poll.id, dispatch, forumAPI);
      dispatch(
        featureContentInOneUp({
          contentId: newPollSession.id,
          contentType: PaneContentType.PollSession,
        }),
      );
    }
  };

  return (
    <Button
      className="mb1 flex-none truncate poll-button"
      kind="classroom-secondary"
      onClick={startPollSession}
      ref={drag}
      data-testid="poll-button"
    >
      {title}
    </Button>
  );
}

function PollSessionButton({ pollSession }: { pollSession: PollSessionType }) {
  const dispatch = useAppDispatch();
  const poll = useAppSelector(classroomSelectors.selectPollById(pollSession.poll_id));

  const [{ isDragging }, drag] = useDrag({
    type: PaneContentType.PollSession,
    item: { id: pollSession.id },
    collect,
  });
  useDragHandler(isDragging);

  const addPollSessionToStage = () => {
    dispatch(
      featureContentInOneUp({
        contentId: pollSession.id,
        contentType: PaneContentType.PollSession,
      }),
    );
  };

  return (
    <Button
      className="mb1 flex-none truncate poll-button"
      kind="classroom-secondary"
      onClick={addPollSessionToStage}
      ref={drag}
    >
      {pollSession.started_at && `[${format(pollSession.started_at, "h:mmaaa")}]`} {poll?.question}
    </Button>
  );
}

function Polls() {
  const polls = Object.values(useAppSelector(classroomSelectors.selectPolls()));
  const pollSessions = Object.values(useAppSelector(classroomSelectors.selectPollSessions()));
  const [newPollType, setNewPollType] = useState<PollTypeType | null>(null);

  const handleShowNewPollForm = (pollType: PollTypeType) => () => {
    setNewPollType(pollType);
  };

  const hideCallback = () => setNewPollType(null);

  return (
    <Flex className="sidebar-width">
      <Flex auto className="vertical-scroll" direction="column" p={2}>
        {(() => {
          switch (newPollType) {
            case PollTypeType.MultipleChoice:
              return (
                <NewPollForm pollType={PollTypeType.MultipleChoice} hideCallback={hideCallback} />
              );
            case PollTypeType.YesNo:
              return <NewPollForm pollType={PollTypeType.YesNo} hideCallback={hideCallback} />;
            case PollTypeType.LongForm:
              return <NewPollForm pollType={PollTypeType.LongForm} hideCallback={hideCallback} />;
            default:
              return (
                <>
                  <h2 className="h2 body-l flex-none mb4">Create a New Poll</h2>
                  <Button
                    className="mb1 flex-none truncate"
                    kind="classroom-secondary"
                    data-testid="mc-poll-button"
                    onClick={handleShowNewPollForm(PollTypeType.MultipleChoice)}
                  >
                    <NotesIcon color="white" />
                    {FRIENDLY_POLL_TYPES[PollTypeType.MultipleChoice]}
                  </Button>
                  <Button
                    className="mb1 flex-none truncate"
                    kind="classroom-secondary"
                    data-testid="yes-no-poll-button"
                    onClick={handleShowNewPollForm(PollTypeType.YesNo)}
                  >
                    <CheckIcon color="white" />
                    {FRIENDLY_POLL_TYPES[PollTypeType.YesNo]}
                  </Button>
                  <Button
                    className="mb1 flex-none truncate"
                    kind="classroom-secondary"
                    data-testid="long-form-poll-button"
                    onClick={handleShowNewPollForm(PollTypeType.LongForm)}
                  >
                    <ChatIcon color="white" />
                    {FRIENDLY_POLL_TYPES[PollTypeType.LongForm]}
                  </Button>
                  <h2 className="h2 body-l flex-none mt5 mb4">Start a Saved Poll</h2>
                  {polls.map((poll) => (
                    <PollButton key={poll.id} poll={poll} />
                  ))}
                  <h2 className="h2 body-l flex-none mt5 mb4">Previous Poll Results</h2>
                  {pollSessions.map((pollSession) => (
                    <PollSessionButton key={pollSession.id} pollSession={pollSession} />
                  ))}
                </>
              );
          }
        })()}
      </Flex>
    </Flex>
  );
}

export { NewPollForm, PollButton, PollSessionButton, Polls };
